import Matter from "matter-js";
import { Assets, Container, Graphics, Sprite, Texture } from "pixi.js";
import type { OrganKind } from "./types";

export interface OrganGeometry {
  asset: string;
  width: number;
  height: number;
  shape: "rect" | "circle";
  radius?: number;
  label: string;
}

const B = import.meta.env.BASE_URL;

export const ORGAN_GEOMETRY: Record<OrganKind, OrganGeometry> = {
  magen: {
    asset: `${B}assets/magen.svg`,
    width: 130,
    height: 130,
    shape: "circle",
    radius: 55,
    label: "Magen",
  },
  leber: {
    asset: `${B}assets/leber.svg`,
    width: 150,
    height: 140,
    shape: "circle",
    radius: 60,
    label: "Leber",
  },
  pankreas: {
    asset: `${B}assets/pankreas.svg`,
    width: 160,
    height: 100,
    shape: "rect",
    label: "Bauchspeicheldrüse",
  },
  darm: {
    asset: `${B}assets/darm.svg`,
    width: 160,
    height: 130,
    shape: "rect",
    label: "Darm",
  },
  ramp: {
    asset: `${B}assets/ramp.svg`,
    width: 160,
    height: 40,
    shape: "rect",
    label: "Bauchwand",
  },
  mund: {
    asset: `${B}assets/mund.svg`,
    width: 130,
    height: 130,
    shape: "circle",
    radius: 50,
    label: "Mund",
  },
  speiseroehre: {
    asset: `${B}assets/speiseroehre.svg`,
    width: 180,
    height: 60,
    shape: "rect",
    label: "Speiseröhre",
  },
};

// Conveyor speed in pixels/second along the speiseröhre's local +x axis
export const SPEISEROEHRE_SPEED = 280;

export async function preloadOrganTextures(): Promise<void> {
  await Promise.all(Object.values(ORGAN_GEOMETRY).map((g) => Assets.load(g.asset)));
}

export class Organ {
  public readonly view: Container;
  public body: Matter.Body | null = null;
  public rotation = 0;
  private glow: Graphics;
  private sprite: Sprite;
  private flashUntil = 0;
  private readonly _scale: number;

  constructor(
    public readonly kind: OrganKind,
    x: number,
    y: number,
    scale = 1,
  ) {
    this._scale = scale;
    const geom = ORGAN_GEOMETRY[kind];
    this.view = new Container();
    this.view.position.set(x, y);

    this.glow = new Graphics();
    this.redrawGlow(0);
    this.view.addChild(this.glow);

    const tex = Assets.get<Texture>(geom.asset) ?? Texture.WHITE;
    this.sprite = new Sprite(tex);
    this.sprite.anchor.set(0.5);
    this.sprite.width = geom.width * scale;
    this.sprite.height = geom.height * scale;
    this.view.addChild(this.sprite);
  }

  get geom(): OrganGeometry {
    return ORGAN_GEOMETRY[this.kind];
  }

  setRotation(rad: number): void {
    this.rotation = rad;
    this.view.rotation = rad;
    if (this.body) Matter.Body.setAngle(this.body, rad);
  }

  setPosition(x: number, y: number): void {
    this.view.position.set(x, y);
    if (this.body) Matter.Body.setPosition(this.body, { x, y });
  }

  createBody(): Matter.Body {
    const g = this.geom;
    const x = this.view.x;
    const y = this.view.y;
    const opts: Matter.IBodyDefinition = {
      isStatic: true,
      friction: this.kind === "ramp" ? 0.05 : 0.2,
      restitution: this.kind === "ramp" ? 0.3 : 0.05,
      label: `organ:${this.kind}`,
      // Darm collects + Speiseröhre transports — both must let particles pass through
      isSensor: this.kind === "darm" || this.kind === "speiseroehre",
    };
    const s = this._scale;
    const body =
      g.shape === "circle" && g.radius
        ? Matter.Bodies.circle(x, y, g.radius * 0.85 * s, opts)
        : Matter.Bodies.rectangle(x, y, g.width * 0.85 * s, g.height * 0.55 * s, opts);
    Matter.Body.setAngle(body, this.rotation);
    (body as Matter.Body & { organ: Organ }).organ = this;
    this.body = body;
    return body;
  }

  syncFromBody(): void {
    if (!this.body) return;
    this.view.position.set(this.body.position.x, this.body.position.y);
    this.view.rotation = this.body.angle;
  }

  flash(nowMs: number, durationMs = 260): void {
    this.flashUntil = nowMs + durationMs;
  }

  tick(nowMs: number): void {
    const remaining = Math.max(0, this.flashUntil - nowMs);
    const alpha = Math.min(1, remaining / 260);
    this.redrawGlow(alpha);
  }

  private redrawGlow(alpha: number): void {
    this.glow.clear();
    if (alpha <= 0) return;
    const g = this.geom;
    const r = (g.shape === "circle" ? (g.radius ?? 60) + 10 : Math.max(g.width, g.height) * 0.6) * this._scale;
    this.glow.circle(0, 0, r).fill({ color: 0xfff3a8, alpha: alpha * 0.55 });
  }
}
